<div class="event-registration-container">
  <!-- Header -->
  <div class="header">
    <h1>Event Registration</h1>
    <button class="btn-back" (click)="router.navigate(['/shows'])">
      <i class="fas fa-arrow-left"></i> Back to Events
    </button>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoadingEvent || isLoadingClasses || isLoadingServices" class="loading-container">
    <div class="spinner"></div>
    <p>Loading event details...</p>
  </div>

  <!-- Event Details -->
  <div *ngIf="event && !isLoadingEvent" class="event-details">
    <div class="event-header">
      <img *ngIf="event.image_url" [src]="event.image_url" alt="{{ event.event_name }}" class="event-image" />
      <div class="event-info">
        <h2>{{ event.event_name }}</h2>
        <div class="event-meta">
          <div class="meta-item" *ngIf="event.company">
            <i class="fas fa-building"></i>
            <span class="organized-by">Organized by <strong>{{ event.company.company_name }}</strong></span>
          </div>
          <div class="meta-item">
            <i class="fas fa-calendar"></i>
            <span>{{ event.start_date | date:'mediumDate' }} - {{ event.end_date | date:'mediumDate' }}</span>
          </div>
          <div class="meta-item">
            <i class="fas fa-map-marker-alt"></i>
            <a [href]="getGoogleMapsLink(event)" target="_blank" class="address-link">
              {{ event.address }}, {{ event.city }}, {{ event.country }}
            </a>
          </div>
          <div class="meta-item">
            <i class="fas fa-clock"></i>
            <span>Registration Deadline: {{ event.registration_deadline | date:'mediumDate' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Description Card -->
    <div class="event-description-card">
      <h3>About This Event</h3>
      <div class="event-description-content" [innerHTML]="event.event_description"></div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="content-grid">
    <!-- Left Column: Classes and Services -->
    <div class="left-column">
      <!-- Event Classes -->
      <div class="section">
        <h3>Event Classes</h3>
        <p class="section-description">Select classes to register for. You can register with any horse in the system.</p>

        <div *ngIf="eventClasses.length === 0" class="empty-state">
          <p>No classes available for this event.</p>
        </div>

        <div class="class-list">
          <div *ngFor="let eventClass of eventClasses" class="class-card">
            <div class="class-card-content">
              <div class="class-info">
                <div class="class-header">
                  <h4>{{ eventClass.class_name }}</h4>
                </div>
                <p class="class-description">{{ eventClass.class_description }}</p>

                <div class="class-details">
                  <div class="detail-item" *ngIf="eventClass.max_num_registrations">
                    <i class="fas fa-users"></i>
                    <span>{{ eventClass.event_class_registrations?.length || 0 }} / {{ eventClass.max_num_registrations }} registered</span>
                  </div>
                  <div class="detail-item" *ngIf="eventClass.req_horse_registration_id">
                    <i class="fas fa-certificate"></i>
                    <span>Requires specific horse registry</span>
                  </div>
                  <div class="detail-item" *ngIf="eventClass.start_date_time">
                    <i class="fas fa-clock"></i>
                    <span>{{ eventClass.start_date_time | date:'short' }}</span>
                  </div>
                </div>
              </div>

              <div class="class-actions">
                <div class="class-price">{{ eventClass.price }} {{ eventClass.price_currency }}</div>
                <button
                  class="btn-add-class-small"
                  (click)="addClassRegistration(eventClass)"
                  [disabled]="isClassFull(eventClass)">
                  <i class="fas fa-plus"></i>
                  {{ isClassFull(eventClass) ? 'Full' : 'Add' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Event Services -->
      <div class="section">
        <h3>Event Services</h3>
        <p class="section-description">Add services to your registration. You can register for multiple slots.</p>

        <div *ngIf="eventServices.length === 0" class="empty-state">
          <p>No services available for this event.</p>
        </div>

        <div class="service-list">
          <div *ngFor="let eventService of eventServices" class="service-card">
            <div class="service-card-content">
              <div class="service-info">
                <div class="service-header">
                  <h4>{{ eventService.event_service_name }}</h4>
                </div>
                <p class="service-description">{{ eventService.event_service_description }}</p>

                <div class="service-details">
                  <div class="detail-item" *ngIf="eventService.max_num_registrations">
                    <i class="fas fa-users"></i>
                    <span>{{ getAvailableServiceSlots(eventService) }} slots available</span>
                  </div>
                </div>
              </div>

              <div class="service-actions">
                <div class="service-price">{{ eventService.price }} {{ eventService.price_currency }}</div>
                <button
                  class="btn-add-service-small"
                  (click)="addServiceRegistration(eventService)"
                  [disabled]="isServiceFull(eventService)">
                  <i class="fas fa-plus"></i>
                  {{ isServiceFull(eventService) ? 'Full' : 'Add' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Registration Summary -->
    <div class="right-column">
      <div class="summary-card sticky">
        <h3>Registration Summary</h3>

        <!-- Class Registrations -->
        <div class="summary-section" *ngIf="classRegistrations.length > 0">
          <h4>Class Registrations ({{ classRegistrations.length }})</h4>

          <div *ngFor="let reg of classRegistrations; let i = index" class="registration-item" [class.collapsed]="!reg.expanded">
            <div class="registration-header" (click)="toggleClassRegistration(reg)">
              <div class="header-content">
                <i class="fas" [class.fa-chevron-down]="reg.expanded" [class.fa-chevron-right]="!reg.expanded"></i>
                <span class="registration-name">{{ reg.class_name }}</span>
              </div>
              <button class="btn-remove" (click)="removeClassRegistration(i); $event.stopPropagation()">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Collapsed Summary -->
            <div class="collapsed-summary" *ngIf="!reg.expanded">
              <div class="summary-line compact">
                <span class="summary-text">{{ reg.user_name || 'No rider' }} • {{ reg.horse_name || 'No horse' }}</span>
                <span class="value price">{{ reg.price }} {{ reg.price_currency }}</span>
              </div>
            </div>

            <!-- Expanded Details -->
            <div class="expanded-details" *ngIf="reg.expanded">
              <!-- User Selection -->
              <div class="form-group">
                <label>Register For *</label>
                <select
                  [(ngModel)]="reg.user_id"
                  (change)="onUserSelected(reg, reg.user_id)"
                  class="form-control">
                  <option *ngFor="let member of familyMembers" [value]="member.user_id">
                    {{ member.name }}
                  </option>
                </select>
              </div>

              <!-- Horse Selection -->
              <div class="form-group">
                <label>Select Horse *</label>
                <select
                  [(ngModel)]="reg.horse_id"
                  (ngModelChange)="onHorseSelected(reg, $event)"
                  class="form-control"
                  [class.is-invalid]="reg.error">
                  <option [ngValue]="null">Select a horse...</option>
                  <option *ngFor="let horse of allHorses" [ngValue]="horse.horse_id">
                    {{ horse.name }}
                  </option>
                </select>
                <div class="error-message" *ngIf="reg.error">
                  {{ reg.error }}
                </div>
              </div>

              <div class="registration-footer">
                <span class="price">{{ reg.price }} {{ reg.price_currency }}</span>
              </div>
            </div>
          </div>

          <button class="btn-add-horse" (click)="openAddHorseModal()">
            <i class="fas fa-plus"></i> Add New Horse
          </button>
        </div>

        <!-- Service Registrations -->
        <div class="summary-section" *ngIf="serviceRegistrations.length > 0">
          <h4>Service Registrations ({{ serviceRegistrations.length }})</h4>

          <div *ngFor="let reg of serviceRegistrations; let i = index" class="registration-item" [class.collapsed]="!reg.expanded">
            <div class="registration-header" (click)="toggleServiceRegistration(reg)">
              <div class="header-content">
                <i class="fas" [class.fa-chevron-down]="reg.expanded" [class.fa-chevron-right]="!reg.expanded"></i>
                <span class="registration-name">{{ reg.service_name }}</span>
              </div>
              <button class="btn-remove" (click)="removeServiceRegistration(i); $event.stopPropagation()">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Collapsed Summary -->
            <div class="collapsed-summary" *ngIf="!reg.expanded">
              <div class="summary-line compact">
                <span class="summary-text">Quantity: {{ reg.num_registrations }}</span>
                <span class="value price">{{ reg.price_per_unit * reg.num_registrations }} {{ reg.price_currency }}</span>
              </div>
            </div>

            <!-- Expanded Details -->
            <div class="expanded-details" *ngIf="reg.expanded">
              <div class="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  min="1"
                  [(ngModel)]="reg.num_registrations"
                  (change)="updateServiceQuantity(reg, reg.num_registrations)"
                  class="form-control" />
              </div>

              <div class="registration-footer">
                <span class="price">{{ reg.price_per_unit * reg.num_registrations }} {{ reg.price_currency }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="classRegistrations.length === 0 && serviceRegistrations.length === 0" class="empty-summary">
          <i class="fas fa-clipboard-list"></i>
          <p>No registrations added yet</p>
          <p class="empty-subtitle">Add classes or services to get started</p>
        </div>

        <!-- Total -->
        <div class="summary-total" *ngIf="classRegistrations.length > 0 || serviceRegistrations.length > 0">
          <div class="total-row">
            <span class="total-label">Total:</span>
            <span class="total-price">{{ getTotalPrice() }} {{ getCurrency() }}</span>
          </div>
        </div>

        <!-- Terms Agreement -->
        <div class="terms-agreement" *ngIf="classRegistrations.length > 0 || serviceRegistrations.length > 0">
          <div class="terms-link-card">
            <i class="fas fa-file-contract"></i>
            <div class="terms-text">
              <p>By registering, you agree to our</p>
              <a (click)="openTermsModal()" class="terms-link">Terms & Conditions</a>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <button
          class="btn-submit"
          (click)="submitRegistrations()"
          [disabled]="!canSubmit()">
          <span *ngIf="!isSubmitting">
            <i class="fas fa-check"></i> Complete Registration
          </span>
          <span *ngIf="isSubmitting">
            <i class="fas fa-spinner fa-spin"></i> Processing...
          </span>
        </button>
      </div>

      <!-- Event Contacts Card -->
<!--      <div class="contacts-card" *ngIf="eventContacts && eventContacts.length > 0">-->
<!--        <h3><i class="fas fa-address-book"></i> Event Contacts</h3>-->
<!--        <div class="contacts-list">-->
<!--          <div *ngFor="let contact of eventContacts" class="contact-card-item">-->
<!--            <div class="d-flex justify-content-between align-items-start mb-3">-->
<!--              <div class="flex-grow-1">-->
<!--                <h4 class="mb-1">{{ contact.first_name }} {{ contact.last_name }}</h4>-->
<!--                <span class="badge badge-light-primary">{{ contact.role?.role_name || 'N/A' }}</span>-->
<!--              </div>-->
<!--            </div>-->

<!--            <div class="separator mb-3"></div>-->

<!--            <div class="d-flex flex-column gap-2">-->
<!--              <div class="d-flex align-items-center" *ngIf="contact.email">-->
<!--                <i class="fa fa-envelope text-muted me-2"></i>-->
<!--                <a [href]="'mailto:' + contact.email" class="text-gray-700 text-decoration-none">{{ contact.email }}</a>-->
<!--              </div>-->
<!--              <div class="d-flex align-items-center" *ngIf="contact.phone">-->
<!--                <i class="fa fa-phone text-muted me-2"></i>-->
<!--                <a [href]="'tel:' + contact.phone" class="text-gray-700 text-decoration-none">{{ contact.phone }}</a>-->
<!--              </div>-->
<!--            </div>-->
<!--          </div>-->
<!--        </div>-->
<!--      </div>-->
    </div>
  </div>

  <!-- Sponsors Section -->
  <div class="sponsors-section" *ngIf="eventSponsors && eventSponsors.length > 0">
    <h3>Proudly Sponsored By</h3>
    <div class="sponsors-marquee-container">
      <div class="sponsors-marquee">
        <div class="sponsors-track" [class.animate]="shouldAnimateSponsors()" [class.static]="!shouldAnimateSponsors()">
          <div *ngFor="let eventSponsor of eventSponsors" class="sponsor-slide">
            <div class="sponsor-card-animated" *ngIf="eventSponsor.sponsor">
              <div class="sponsor-badge">
                <img *ngIf="eventSponsor.sponsor.logo_url"
                     [src]="eventSponsor.sponsor.logo_url"
                     [alt]="eventSponsor.sponsor.sponsor_name"
                     class="sponsor-logo-animated" />
                <div class="sponsor-shine"></div>
              </div>
              <div class="sponsor-details">
                <h4>{{ eventSponsor.sponsor.sponsor_name }}</h4>
                <p *ngIf="eventSponsor.sponsor.sponsor_description">{{ eventSponsor.sponsor.sponsor_description }}</p>
              </div>
            </div>
          </div>
          <!-- Duplicate for seamless loop (only when animating) -->
          <ng-container *ngIf="shouldAnimateSponsors()">
            <div *ngFor="let eventSponsor of eventSponsors" class="sponsor-slide">
              <div class="sponsor-card-animated" *ngIf="eventSponsor.sponsor">
                <div class="sponsor-badge">
                  <img *ngIf="eventSponsor.sponsor.logo_url"
                       [src]="eventSponsor.sponsor.logo_url"
                       [alt]="eventSponsor.sponsor.sponsor_name"
                       class="sponsor-logo-animated" />
                  <div class="sponsor-shine"></div>
                </div>
                <div class="sponsor-details">
                  <h4>{{ eventSponsor.sponsor.sponsor_name }}</h4>
                  <p *ngIf="eventSponsor.sponsor.sponsor_description">{{ eventSponsor.sponsor.sponsor_description }}</p>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Add Horse Modal -->
<div class="modal-overlay" *ngIf="showAddHorseModal" (click)="closeAddHorseModal()">
  <div class="modal-content" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h3>Add New Horse</h3>
      <button class="btn-close" (click)="closeAddHorseModal()">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <form [formGroup]="addHorseForm" class="modal-body">
      <div class="form-group">
        <label>Horse Name *</label>
        <input type="text" formControlName="name" class="form-control" placeholder="Enter horse name" />
      </div>

      <div class="form-group">
        <label>Date of Birth</label>
        <input type="date" formControlName="date_of_birth" class="form-control" />
      </div>

      <div class="form-group">
        <label>Passport ID</label>
        <input type="text" formControlName="passport_id" class="form-control" placeholder="Enter passport ID" />
      </div>

      <div class="form-group">
        <label>Horse Registry</label>
        <select formControlName="horse_registry_id" class="form-control">
          <option [value]="null">Select registry...</option>
          <option *ngFor="let registry of horseRegistries" [value]="registry.registry_id">
            {{ registry.registry_name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label>Gender *</label>
        <select formControlName="gender" class="form-control">
          <option [value]="null">Select gender...</option>
          <option *ngFor="let gender of horseGenders" [value]="gender.id">
            {{ gender.name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label>Breed</label>
        <input type="text" formControlName="breed" class="form-control" placeholder="Enter breed" />
      </div>

      <div class="form-group">
        <label>Color</label>
        <input type="text" formControlName="color" class="form-control" placeholder="Enter color" />
      </div>
    </form>

    <div class="modal-footer">
      <button class="btn-cancel" (click)="closeAddHorseModal()">Cancel</button>
      <button class="btn-save" (click)="submitNewHorse()" [disabled]="addHorseForm.invalid">
        <i class="fas fa-save"></i> Add Horse
      </button>
    </div>
  </div>
</div>

<!-- Terms Modal -->
<div class="modal-overlay" *ngIf="showTermsModal" (click)="closeTermsModal()">
  <div class="modal-content modal-content-large" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h3>Registration Terms & Conditions</h3>
      <button class="btn-close" (click)="closeTermsModal()">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="modal-body terms-modal-body">
      <div class="terms-content">
        <h4>1. Registration and Payment</h4>
        <ul>
          <li>All registrations are subject to availability and must be completed before the registration deadline.</li>
          <li>Payment must be received in full before the event to confirm your registration.</li>
          <li>We accept major credit cards and bank transfers as payment methods.</li>
        </ul>

        <h4>2. Cancellation Policy</h4>
        <ul>
          <li>Cancellations made more than 14 days before the event will receive a full refund minus processing fees.</li>
          <li>Cancellations made less than 14 days before the event are non-refundable.</li>
          <li>In case of emergency or medical reasons, please contact the event organizer directly.</li>
        </ul>

        <h4>3. Horse Requirements</h4>
        <ul>
          <li>Participants must ensure their horses meet all registry and health requirements for their selected classes.</li>
          <li>Current vaccination records and health certificates must be presented at check-in.</li>
          <li>Horses must have appropriate insurance coverage for participation.</li>
        </ul>

        <h4>4. Event Modifications</h4>
        <ul>
          <li>The event organizer reserves the right to modify or cancel the event due to unforeseen circumstances.</li>
          <li>In case of event cancellation, full refunds will be provided to all registered participants.</li>
          <li>Schedule changes and updates will be communicated via email and posted on the event website.</li>
        </ul>

        <h4>5. Liability and Risk</h4>
        <ul>
          <li>All participants compete at their own risk and must have appropriate insurance coverage.</li>
          <li>The event organizer is not liable for any injuries, damages, or losses incurred during the event.</li>
          <li>Participants agree to release and hold harmless the event organizer from any claims.</li>
        </ul>

        <h4>6. Rules and Conduct</h4>
        <ul>
          <li>By registering, you agree to abide by all event rules and regulations.</li>
          <li>Unsportsmanlike conduct or violation of rules may result in disqualification without refund.</li>
          <li>Decisions made by event officials and judges are final.</li>
        </ul>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn-save" (click)="closeTermsModal()">
        <i class="fas fa-check"></i> I Understand
      </button>
    </div>
  </div>
</div>
